"use strict";
/*
 * Pure analysis helpers for keyboard bot detection.
 *
 * The input is a list of keydown times, in milliseconds, from a single field
 * (the hook resets it when focus moves to another field). Real people type with
 * uneven gaps and can never type faster than a physical limit. Scripts tend to
 * fire many keystrokes with almost no gap, or at a perfectly even rhythm.
 *
 * All thresholds are rough guesses, meant to be tuned with real traffic.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.analyzeKeyboardBehavior = exports.hasUniformCadence = exports.hasConsecutiveFastKeystrokes = exports.MIN_KEYSTROKES_FOR_ANALYSIS = void 0;
const helpers_1 = require("lib/bots/helpers");
// Fewest keystrokes we need before the even-rhythm check makes sense.
exports.MIN_KEYSTROKES_FOR_ANALYSIS = 6;
// Two keystrokes closer than this many milliseconds are faster than a human.
const MIN_KEYSTROKE_GAP_MS = 10;
// How many fast gaps in a row (each under MIN_KEYSTROKE_GAP_MS) look automated. A
// person may overlap two keys once or twice by mistake, but not this many in a
// row with no real gap.
const MIN_FAST_KEYSTROKE_RUN = 5;
// Rhythm variation (a unitless ratio) below this looks machine-even.
const EVEN_RHYTHM_RATIO_THRESHOLD = 0.05;
// Gaps between each pair of consecutive keydown times, in milliseconds.
const getKeystrokeGapsMs = (keydownTimesMs) => {
    const gapsMs = [];
    for (let index = 1; index < keydownTimesMs.length; index += 1) {
        gapsMs.push(keydownTimesMs[index] - keydownTimesMs[index - 1]);
    }
    return gapsMs;
};
// True if there is a run of several keystrokes in a row with almost no gap
// between them — a single overlap is ignored, a sustained burst is not.
const hasConsecutiveFastKeystrokes = (keydownTimesMs) => {
    const gapsMs = getKeystrokeGapsMs(keydownTimesMs);
    let fastRunLength = 0;
    for (const gapMs of gapsMs) {
        if (gapMs < MIN_KEYSTROKE_GAP_MS) {
            fastRunLength += 1;
            if (fastRunLength >= MIN_FAST_KEYSTROKE_RUN) {
                return true;
            }
        }
        else {
            fastRunLength = 0;
        }
    }
    return false;
};
exports.hasConsecutiveFastKeystrokes = hasConsecutiveFastKeystrokes;
// True if the time between keystrokes is almost perfectly even.
const hasUniformCadence = (keydownTimesMs) => {
    if (keydownTimesMs.length < exports.MIN_KEYSTROKES_FOR_ANALYSIS) {
        return false;
    }
    const gapsMs = getKeystrokeGapsMs(keydownTimesMs);
    return (0, helpers_1.coefficientOfVariation)(gapsMs) < EVEN_RHYTHM_RATIO_THRESHOLD;
};
exports.hasUniformCadence = hasUniformCadence;
// True if the keystroke timing shows any bot-like trait.
const analyzeKeyboardBehavior = (keydownTimesMs) => {
    return (0, exports.hasConsecutiveFastKeystrokes)(keydownTimesMs) || (0, exports.hasUniformCadence)(keydownTimesMs);
};
exports.analyzeKeyboardBehavior = analyzeKeyboardBehavior;
